/**
 * Dailymotion video library — pure helpers (formatting + embed building).
 * Kept side-effect free so they can be unit-tested in isolation.
 */
import * as moment from 'moment';
import { DM_BODY_PLAYER, DM_CHANNELS, DM_MAX_RESULTS, DM_PAGE_SIZE } from './constants';

/**
 * What the list footer shows: nothing (empty list), a "load more" button,
 * "end of results", or "refine the search" when the API's 1,000-results cap is
 * what stopped the pagination (more videos exist but are unreachable).
 */
export const getFooterState = (count: number, page: number, hasMore: boolean): 'none' | 'loadMore' | 'end' | 'refine' => {
  if (!count) {
    return 'none';
  }
  if (hasMore) {
    return 'loadMore';
  }
  return page * DM_PAGE_SIZE >= DM_MAX_RESULTS ? 'refine' : 'end';
};

/** Minimal video shape the channel/embed helpers rely on. */
interface IDmVideoOwner {
  owner?: string;
  'owner.screenname'?: string;
  allow_embed?: boolean;
}

/**
 * Whether the video can be played by the article player. Videos whose channel
 * disabled embedding (`allow_embed: false`) would render a broken player in
 * the article, so they are hidden. A missing flag is treated as embeddable.
 */
export const isEmbeddable = (video: IDmVideoOwner): boolean => video.allow_embed !== false;

/**
 * Channel label shown in the card tag: the configured label of the owner
 * channel, falling back to the Dailymotion screenname for an unknown owner.
 */
export const getChannelLabel = (video: IDmVideoOwner): string => {
  const channel = DM_CHANNELS.find(c => c.id === video.owner);
  return channel ? channel.label : video['owner.screenname'] || '';
};

/**
 * Build the embed code to paste in an article body.
 * IMPORTANT: must be exactly the `x6svc` iframe form (see DM_BODY_PLAYER).
 * Example: buildEmbedCode('xajw70y')
 *   => <iframe src="https://geo.dailymotion.com/player/x6svc.html?video=xajw70y"></iframe>
 */
export const buildEmbedCode = (videoId: string): string =>
  `<iframe src="https://geo.dailymotion.com/player/${DM_BODY_PLAYER}.html?video=${videoId}"></iframe>`;

/** Public watch URL on dailymotion.com (used by the "open externally" action). */
export const buildWatchUrl = (videoId: string): string =>
  `https://www.dailymotion.com/video/${videoId}`;

/** Format a duration in seconds as `m:ss` (or `h:mm:ss` past one hour). */
export const formatDuration = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined || isNaN(seconds)) {
    return '';
  }
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

/** Compact view counter: 1 234 → "1,2 k", 1 200 000 → "1,2 M". */
export const formatViews = (views?: number | null): string => {
  if (views === null || views === undefined || isNaN(views)) {
    return '0';
  }
  const strip = (n: number): string => n.toFixed(1).replace('.0', '').replace('.', ',');
  if (views >= 1000000) {
    return `${strip(views / 1000000)} M`;
  }
  if (views >= 1000) {
    return `${strip(views / 1000)} k`;
  }
  return String(views);
};

/**
 * Format the publication date from a Dailymotion `created_time` (epoch seconds).
 * Numeric DD/MM/YYYY → locale-independent and deterministic.
 */
export const formatPublishedDate = (epochSeconds?: number | null): string => {
  if (!epochSeconds) {
    return '';
  }
  return moment.unix(epochSeconds).format('DD/MM/YYYY');
};

/**
 * Copy the embed code to the clipboard, with a graceful fallback for
 * non-secure contexts where navigator.clipboard is unavailable.
 * Returns the copied string.
 */
export const copyEmbedToClipboard = async (videoId: string): Promise<string> => {
  const code = buildEmbedCode(videoId);
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(code);
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = code;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
  return code;
};
